import { Rnd } from 'react-rnd';
import '../WidgetStyles.css';
import React, { FC, useEffect, useRef, useState } from 'react';
import { RootState } from 'shared/state';
import _ from 'lodash';
import styled from 'styled-components';
import { SizeLocationData } from 'builder/components/WidgetSizeLocation';
import { getMaxXYLocation, getValue } from '../SiteBuilderHelper';
import {
  FOOTER_SECTION_ID,
  HEADER_PLACEHOLDER_ID,
  HEADER_SECTION_ID,
  DEFAULT_CUSTOM_WIDGET_MESSAGE,
  min_dy,
  widgetOutsideWarning,
  widgetTypes,
} from 'builder/util/constants';
import uuidv4 from 'uuid/v4';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { User } from 'oidc-client';
import { updateSite } from 'shared/state/ducks/sites';
import {
  getAllChildrenRecursively,
  getAncestorCount,
  groupWidgets,
  ungroupWidgets,
} from 'shared/state/ducks/widgets';
import { deleteCalendarWidget } from 'shared/api/calendar';
import { useDispatch, useSelector } from 'react-redux';
import {
  BELOW_HEADER_PLACEMENT,
  STICKY_BOTTOM_PLACEMENT,
} from 'shared/widgets/Banner/types';
import {
  setSelectedWidgetIds,
  getSelectedWidgetIds,
} from '../../../../shared/state/ducks/widgets';
import { deleteContentIfExists } from 'shared/api/horizonContentApi';
import {
  syncRepeaterLayout,
  syncDeleteAcrossRepeaterContainers,
  syncAddAcrossRepeaterContainers,
} from 'shared/widgets/RepeaterWidget/RepeaterUtils';
import WidgetWrapperContextMenu from './WidgetWrapperContextMenu';
import {
  injectTracking,
  shiftDescendantWidgetLocations,
  shouldPropagateDefaultWidgetMove,
} from './WidgetWrapper.util';
// Reverted deepCopy helper; original implementation relied directly on lodash cloneDeep where needed.

const StyledSpan = styled.span`
  width: 100%;
  height: 100%;
`;

const RichTextEditorSpan = styled.span`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: inline-block;
`;

const StyledRnd = styled(Rnd)`
  transform: ${p =>
    p.fullScreenEnabled
      ? `translate(0px, ${p.itemYLocation}px) !important`
      : `translate(${p.itemXLocation}px, ${p.itemYLocation}px)`};
`;

const complexWidgetTypes = [
  widgetTypes.Section,
  widgetTypes.Card,
  widgetTypes.AccordionWidget,
  widgetTypes.Tabs,
  widgetTypes.Form,
  widgetTypes.Banner,
  widgetTypes.Slideshow,
];

export const saveWidgetToMyWidgets = ({
  widgetState,
  currentPage,
  siteId,
  site,
  dispatch,
}) => {
  const widgetToCustomise = widgetState;
  if (!widgetToCustomise) return;

  const unique = uuidv4();
  const customWidget = {
    ...widgetToCustomise,
    id: unique,
    config: {
      ...widgetToCustomise.config,
      isCustomWidget: true,
      customWidgetName: `Custom ${widgetToCustomise.type} Widget`,
      customWidgetDescription: DEFAULT_CUSTOM_WIDGET_MESSAGE,
    },
  };

  const createCustomChildWidget = childId => {
    const childWidgetId = uuidv4();
    const childWidget = currentPage.content[childId];
    return {
      ...childWidget,
      id: childWidgetId,
      config: {
        ...childWidget.config,
        isCustomWidget: true,
      },
    };
  };

  const customWidgetsArray = [];
  if (complexWidgetTypes.includes(customWidget.type)) {
    customWidget.children = customWidget.children.map(childId => {
      const customChildWidget = createCustomChildWidget(childId);
      customWidgetsArray.push({
        widgetId: customChildWidget.id,
        widgetName: `Custom ${customChildWidget.type} Widget`,
        widgetDescription: DEFAULT_CUSTOM_WIDGET_MESSAGE,
        widgetConfig: customChildWidget,
      });
      return customChildWidget.id;
    });
  }

  customWidgetsArray.push({
    widgetId: unique,
    widgetName: `Custom ${widgetToCustomise.type} Widget`,
    widgetDescription: DEFAULT_CUSTOM_WIDGET_MESSAGE,
    widgetConfig: customWidget,
  });

  dispatch(
    updateSite(siteId, {
      ...site,
      customWidgets: site.customWidgets
        ? [...site.customWidgets, ...customWidgetsArray]
        : customWidgetsArray,
    })
  );
};

export const handleDownloadWidget = (widgetState, currentPage) => {
  if (widgetState.children) {
    widgetState['childArray'] = widgetState.children.map(childId => {
      return currentPage.content[childId];
    });
  }
  const fileData = JSON.stringify(widgetState);
  const blob = new Blob([fileData], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = `${widgetState.type}Widget.json`;
  link.href = url;
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const getBorderStyles = (
  widgetState: any,
  isActiveWidget: boolean,
  hoveredWidget: string,
  isBorderDisabled: boolean
) => {
  switch (true) {
    case isActiveWidget:
      return 'solid 3px #0C55B8';
    case widgetState.id === hoveredWidget && !isActiveWidget:
      return 'solid 2px #DBF0FF';
    case widgetState.type === widgetTypes.CalendarWidget:
      return 'solid 1px #000000';
    case !isBorderDisabled:
      return 'solid 1px #000000';
    default:
      return '';
  }
};

export function duplicateWidgetWithChildren({
  widgetToDuplicate,
  currentPage,
  addChild,
  parentWidgetId,
}) {
  const duplicateRecursively = (
    widget: Widget<any>,
    parentId: string | null
  ): string => {
    const newId = uuidv4();
    const newConfig = {
      ...widget.config,
      xLocation: widget.config.xLocation + 10,
      yLocation: widget.config.yLocation + 10,
      isWidgetDuplicated: true,
      parentId: parentId,
    };
    // Duplicating a sticky banner would create a second one, which PageLayout
    // can't render correctly (only one banner's children get relocated).
    if (
      widget.type === widgetTypes.Banner &&
      newConfig.placement === STICKY_BOTTOM_PLACEMENT
    ) {
      newConfig.placement = BELOW_HEADER_PLACEMENT;
    }
    addChild(widget.type, newConfig, 0, newId, parentId, widget.tabIndex);
    if (widget.children && widget.children.length > 0) {
      widget.children.forEach(childId => {
        const childWidget = currentPage.content[childId];
        if (!childWidget) {
          return null;
        }
        return duplicateRecursively(childWidget, newId);
      });
    }
    return newId;
  };

  duplicateRecursively(widgetToDuplicate, parentWidgetId);
}

export interface WidgetWrapperProps<T> {
  accordionHeightShift: number;
  accordionShiftWidgetId: string;
  activeWidgetId: string;
  addChild: (
    type: string,
    initialConfig: T,
    location: number,
    widgetId: string,
    newParentWidgetId: string,
    tabIndex: number
  ) => void;
  addSibling?: (
    type: string,
    config: T,
    siblingWidgetId: string,
    children: string[],
    newWidgetId: string,
    tabIndex: number
  ) => void;
  closeConfigPanel?: (open: boolean) => void;
  component: any;
  currentPage: PageVersion;
  getActiveWidgetId?: (widgetId: string) => void;
  getSizeLocation?: (sizeLoc: SizeLocationData) => void;
  initiateLogout: () => void;
  isAccordionPanelOpen: boolean;
  isEditingTemplate: boolean;
  locationCoords?: any;
  onClick?: (e: React.MouseEvent) => void;
  onUpdateConfig?: (
    config: any,
    widgetId: string,
    defaultWidget: boolean
  ) => void;
  openPopup?: (open: boolean) => void;
  page?: Page;
  pages: Array<Page>;
  removeDefaultWidgets?: (widgetId: string) => void;
  removeWidget?: (widgetId: string) => void;
  setResetToFalse?: () => void;
  shouldDisplayGridLines: boolean;
  site: Site;
  siteId: number;
  updateCurrentPage?: (pageVersion: PageVersion) => void;
  updateCurrentPageWidgetConfig?: (config: any, widgetId: string) => void;
  updatePageDefaultWidget: (
    widgetId: string,
    config: any,
    xDifference: number,
    yDifference: number
  ) => void;
  updateSizeLocation?: SizeLocationData;
  updateWidgetPosition?: (
    bannerWidgetId: string,
    height: number,
    oldHeight: number,
    newPlacement?: string
  ) => void;
  user?: User;
  widgetId: string;
  widgetState: Widget<T>;
  editingMode?: boolean;
  normalizeHeaderFooter?: () => void;
  // Tracking object injected from SiteLandingPage; provides trackEvent for analytics.
  tracking?: any;
}

const isHorizontalNavWidget = (item: any, currentPage: any): boolean =>
  item.widgetType === widgetTypes.Navigation &&
  currentPage.content[item.uniqueId]?.config?.style === 'Horizontal';

const isSearchWidget = (item: any): boolean =>
  item.widgetType === widgetTypes.Search;

export const WidgetWrapper: FC<WidgetWrapperProps<any>> = (
  props: WidgetWrapperProps<any>
) => {
  const {
    accordionHeightShift,
    accordionShiftWidgetId,
    activeWidgetId,
    addChild,
    closeConfigPanel,
    component,
    currentPage,
    getActiveWidgetId,
    isAccordionPanelOpen,
    isEditingTemplate,
    locationCoords,
    openPopup,
    pages,
    removeDefaultWidgets,
    removeWidget,
    setResetToFalse,
    shouldDisplayGridLines,
    site,
    siteId,
    updateCurrentPage,
    updatePageDefaultWidget,
    updateWidgetPosition,
    widgetId,
    widgetState,
    normalizeHeaderFooter,
  } = props;

  const [widgetList, setWidgetList] = useState([]);
  const [hoveredWidget, setHoveredWidget] = useState(null);
  const [hoveredHorizontalNavId, setHoveredHorizontalNavId] = useState<
    string | null
  >(null);
  const [hoveredSearchWidgetId, setHoveredSearchWidgetId] = useState<
    string | null
  >(null);
  const [showLines, setShowLines] = useState(false);
  const [gridlinesPosition, setGridlinesPosition] = useState({ x: 0, y: 0 });
  const rndRef = useRef<Rnd>(null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [currentWidget, setCurrentWidget] = useState('');
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: -Number.MAX_SAFE_INTEGER,
    y: -Number.MIN_SAFE_INTEGER,
  });

  const contextMenuRef = useRef<HTMLDivElement>(null);
  const selectedWidgetIds = useSelector((state: RootState) =>
    getSelectedWidgetIds(state.widgets)
  ).filter(id => currentPage?.content[id]?.config.visible !== false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedWidgetIds.length > 0) {
      getActiveWidgetId('root');
    }
  }, [selectedWidgetIds.length === 0]);

  React.useEffect(() => {
    if (rndRef.current) {
      rndRef.current.offsetFromParent = { top: 0, left: 0 };
      rndRef.current.updateOffsetFromParent();
    }
  }, [rndRef.current]);

  React.useEffect(() => {
    if (locationCoords !== undefined) {
      if (
        locationCoords.widgetType === widgetTypes.Banner &&
        widgetState.config.dateSelection
      ) {
        const currentDate = new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate()
        );
        const endDate = new Date(widgetState.config.dateSelection.endDate);
        if (currentDate !== endDate && currentDate > endDate) {
          handleDeleteWidget(widgetState.id, false);
        }
      }
      let widgetListArr = [...widgetList];
      initialWidgetConfig.x = locationCoords.x;
      initialWidgetConfig.y = locationCoords.y;
      initialWidgetConfig.originalYLocation = locationCoords.originalYLocation;

      initialWidgetConfig.width = locationCoords.width;
      initialWidgetConfig.height = locationCoords.height;
      initialWidgetConfig.minWidth = locationCoords.minWidth;
      initialWidgetConfig.minHeight = locationCoords.minHeight;
      initialWidgetConfig.widgetType = locationCoords.widgetType;
      initialWidgetConfig.uniqueId = widgetId;
      initialWidgetConfig.content = component;
      initialWidgetConfig.zIndex = locationCoords.zIndex;
      initialWidgetConfig.isWidgetDuplicated = locationCoords.isWidgetDuplicated
        ? locationCoords.isWidgetDuplicated
        : false;
      widgetListArr.push(initialWidgetConfig);
      setWidgetList([...widgetListArr]);
    }
  }, [locationCoords]);

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyboardEvent);
    return () => {
      document.removeEventListener('keydown', handleKeyboardEvent);
    };
  }, []);

  React.useEffect(() => {
    const handleClickOutside = event => {
      if (
        showContextMenu &&
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setShowContextMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showContextMenu]);

  const isActiveWidget =
    selectedWidgetIds.includes(widgetId) || widgetState.id === activeWidgetId;

  const initialWidgetConfig = {
    width: 180,
    height: 80,
    minWidth: 175,
    minHeight: 80,
    x: 2,
    y: 10,
    content: component,
    uniqueId: 'Text',
    widgetType: 'Text',
    zIndex: 0,
    isWidgetDuplicated: false,
    originalYLocation: 10,
  };

  const lockedLayoutResizingOptions = {
    top: false,
    right: false,
    bottom: true,
    left: false,
    topRight: false,
    bottomRight: false,
    bottomLeft: false,
    topLeft: false,
  };

  const getFullScreenEnabled = (widgetType: string) => {
    let isFullScreen = false;
    if (
      [
        widgetTypes.Image,
        widgetTypes.Divider,
        widgetTypes.Navigation,
        widgetTypes.Section,
        widgetTypes.Banner,
        widgetTypes.TemplateHeaderFooterPlaceholder,
      ].includes(widgetType)
    ) {
      isFullScreen = widgetState.config.enableFullScreen;
    }
    return isFullScreen;
  };

  const getWidgetsIdsBelowAccordion = (widgetId: string) => {
    const headerSectionAndChildIds = currentPage.content[HEADER_SECTION_ID]
      ? [
          ...(currentPage.content[HEADER_SECTION_ID]?.children ?? []),
          HEADER_SECTION_ID,
        ]
      : [];
    const widgetsExceptHeader = Object.keys(currentPage.content).filter(
      id => !headerSectionAndChildIds.includes(id) && id !== 'root'
    );
    let thresholdY =
      currentPage.content[widgetId]?.config?.yLocation +
      currentPage.content[widgetId]?.config?.height;
    const widgetsBelowAccordion = widgetsExceptHeader
      .map(widgetId => currentPage.content[widgetId])
      .filter(widget => widget?.config?.yLocation > thresholdY);
    return widgetsBelowAccordion.map(widget => widget.id);
  };

  const getShiftedYposition = (widgetId: string, yCoordinates: number) => {
    const widgetsIdsToShift = getWidgetsIdsBelowAccordion(
      accordionShiftWidgetId
    );
    if (widgetsIdsToShift.includes(widgetId)) {
      const updatedCoordinates = yCoordinates + accordionHeightShift;
      return updatedCoordinates;
    } else {
      return yCoordinates;
    }
  };

  const transformStyle = 'translate(146px, 44px)';

  const style = {
    display: `${
      widgetState?.type === widgetTypes.TextInput ? 'inline-block' : 'flex'
    }`,
    alignItems: 'center',
    justifyContent: 'center',
    border:
      props.editingMode && widgetState.type !== widgetTypes.Divider
        ? getBorderStyles(
            widgetState,
            isActiveWidget,
            hoveredWidget,
            site.disableBorder
          )
        : 'none',
    outline:
      isActiveWidget &&
      props.editingMode &&
      widgetState.type === widgetTypes.Divider
        ? 'solid 3px #0C55B8'
        : props.editingMode && widgetState.type === widgetTypes.Divider
        ? '1px solid #000'
        : 'none',
    transform: transformStyle,
    width: getFullScreenEnabled(widgetState?.type) ? '100vw' : 'auto',
    top:
      isAccordionPanelOpen && widgetState.id === FOOTER_SECTION_ID
        ? accordionHeightShift
        : 'auto',
    left: getFullScreenEnabled(widgetState?.type)
      ? props.editingMode
        ? 'calc(-50vw + 47.2%)'
        : 'calc(-50vw + 50%)'
      : '0px',
  };

  const handleKeyboardEvent = (event: KeyboardEvent) => {
    if (activeWidgetId === 'root') return;
    if ((event.metaKey || event.ctrlKey) && event.code === 'KeyD') {
      handleDuplicateWidget(activeWidgetId);
    } else if ((event.metaKey || event.ctrlKey) && event.code === 'Backspace') {
      handleDeleteWidget(activeWidgetId, true);
    }
  };

  const onDragWidget = (d, widgetId) => {
    setShowLines(true);
    setGridlinesPosition({
      x: d.x,
      y: d.y,
    });
    const widget = document.getElementById(widgetId);
    widget.style.zIndex = '1000';
    if (
      currentPage.layoutLocked &&
      currentPage.content[widgetId].config.originalYLocation > d.y
    ) {
      return false;
    }
  };

  const handleResize = d => {
    setShowLines(true);
    setGridlinesPosition({
      x: d.x,
      y: d.y,
    });
  };

  const validateLocation = (xLocation: number, widgetId: string) => {
    const maxXYLocation = getMaxXYLocation('editingContainerId', widgetId, 0);
    if (xLocation < -3 || maxXYLocation[0] < -1) {
      const customId = 'custom-id-yes';
      toast(widgetOutsideWarning, {
        toastId: customId,
        type: 'warning',
        autoClose: 5000,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
    } else {
      toast.dismiss();
    }
  };

  const updateLocation = (d, index, widgetId, widgetType) => {
    if (
      ![
        widgetTypes.Image,
        widgetTypes.Divider,
        widgetTypes.Navigation,
        widgetTypes.Banner,
        widgetTypes.Section,
      ].includes(widgetType)
    ) {
      validateLocation(d.x, widgetId);
    }
    setShowLines(true);
    const itemsCopy = [...widgetList];
    if (itemsCopy[index].x === d.x && itemsCopy[index].y === d.y) return;
    itemsCopy[index].x = d.x;
    itemsCopy[index].y = d.y;
    setWidgetList(itemsCopy);
    let newCurrentPage = currentPage;
    const oldCurrentPage = currentPage;
    const widgetConfigOld = currentPage.content[widgetId].config;
    const widgetConfigNew = {
      ...widgetConfigOld,
      ...{
        xLocation: d.x,
        yLocation: d.y < min_dy ? min_dy : d.y,
        marginTop: 0,
      },
    };
    const xLocationDiff = widgetConfigNew.xLocation - widgetConfigOld.xLocation;
    const yLocationDiff = widgetConfigOld.marginTop
      ? widgetConfigNew.yLocation -
        widgetConfigOld.yLocation -
        widgetConfigOld.marginTop
      : widgetConfigNew.yLocation - widgetConfigOld.yLocation;

    setResetToFalse();

    // Checking if this is a repeater widget BEFORE updating the page state
    const tempWidget = {
      ...newCurrentPage.content[widgetId],
      config: widgetConfigNew,
    };
    const repeaterWidgetId = tempWidget.config?.repeaterWidgetId;
    const repeaterSectionId = tempWidget.config?.repeaterWidgetIndex;

    let batchHandled = false;
    if (repeaterWidgetId || repeaterSectionId) {
      batchHandled = syncRepeaterLayout({
        changedWidget: tempWidget,
        currentPage: {
          ...newCurrentPage,
          content: { ...newCurrentPage.content, [widgetId]: tempWidget },
        },
        oldCurrentPage: oldCurrentPage,
        site: props.site,
        pageId: currentPage.pageId,
        dispatch,
      });
    }

    if (!batchHandled) {
      newCurrentPage = shiftDescendantWidgetLocations(
        currentPage,
        getAllChildrenRecursively(currentPage, widgetId),
        xLocationDiff,
        yLocationDiff
      );
      newCurrentPage = {
        ...newCurrentPage,
        content: {
          ...newCurrentPage.content,
          [widgetId]: {
            ...newCurrentPage.content[widgetId],
            config: widgetConfigNew,
          },
        },
      };
      updateCurrentPage(newCurrentPage);
    }

    if (shouldPropagateDefaultWidgetMove(newCurrentPage.content, widgetId)) {
      updatePageDefaultWidget(
        widgetId,
        widgetConfigNew,
        xLocationDiff,
        yLocationDiff
      );
      normalizeHeaderFooter?.();
    }
  };

  const getEnableResizing = (widgetType: string) => {
    if (widgetType === widgetTypes.Application) {
      return false;
    } else if (currentPage.layoutLocked) {
      return lockedLayoutResizingOptions;
    }
    return true;
  };

  const updateSize = (ref, position, index, widgetId, widgetType) => {
    if (
      ![
        widgetTypes.Image,
        widgetTypes.Divider,
        widgetTypes.Navigation,
        widgetTypes.Section,
        widgetTypes.Banner,
      ].includes(widgetType)
    ) {
      validateLocation(position.x, widgetId);
    }
    let refactorHeightOnResizeOverTopBorder = 0;
    if (position.y < min_dy) {
      refactorHeightOnResizeOverTopBorder =
        getValue(ref.style.height) + position.y;
    } else {
      refactorHeightOnResizeOverTopBorder = getValue(ref.style.height);
    }
    const itemsCopy = _.cloneDeep([...widgetList]);
    itemsCopy[index].width = getValue(ref.style.width);
    itemsCopy[index].height = refactorHeightOnResizeOverTopBorder;
    itemsCopy[index].x = position.x;
    itemsCopy[index].y = position.y < min_dy ? min_dy : position.y;
    setWidgetList(itemsCopy);

    const { currentPage } = props;
    const newCurrentPage = _.cloneDeep(currentPage);
    const oldCurrentPage = currentPage;
    const widgetConfigOld = newCurrentPage.content[widgetId].config;
    const widgetConfigNew = {
      ...widgetConfigOld,
      ...{
        width: getValue(ref.style.width),
        height: refactorHeightOnResizeOverTopBorder,
        xLocation: position.x,
        yLocation: position.y < min_dy ? min_dy : position.y,
      },
    };
    const xLocationDiff = widgetConfigNew.xLocation - widgetConfigOld.xLocation;
    const yLocationDiff = widgetConfigNew.yLocation - widgetConfigOld.yLocation;

    setResetToFalse();

    // Checking if this is a repeater widget BEFORE updating the page state
    const tempWidget = {
      ...newCurrentPage.content[widgetId],
      config: widgetConfigNew,
    };
    const repeaterWidgetId = tempWidget.config?.repeaterWidgetId;
    const repeaterSectionId = tempWidget.config?.repeaterWidgetIndex;

    let batchHandled = false;
    if (repeaterWidgetId || repeaterSectionId) {
      batchHandled = syncRepeaterLayout({
        changedWidget: tempWidget,
        currentPage: {
          ...newCurrentPage,
          content: { ...newCurrentPage.content, [widgetId]: tempWidget },
        },
        oldCurrentPage: oldCurrentPage,
        site: props.site,
        pageId: currentPage.pageId,
        dispatch,
      });
    }

    if (!batchHandled) {
      newCurrentPage.content[widgetId].config = widgetConfigNew;
      updateCurrentPage(newCurrentPage);
    }

    if (
      newCurrentPage.content[widgetId].defaultWidget &&
      (newCurrentPage.content[widgetId].id !== FOOTER_SECTION_ID ||
        xLocationDiff === 0 ||
        yLocationDiff === 0)
    ) {
      updatePageDefaultWidget(
        widgetId,
        widgetConfigNew,
        xLocationDiff,
        yLocationDiff
      );
    }
    if (widgetType === widgetTypes.Banner) {
      updateWidgetPosition(
        widgetId,
        getValue(ref.style.height) - widgetConfigOld.height,
        0
      );
    }
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault(); // Prevent the default browser context menu from showing
    setShowContextMenu(true);
  };

  const handleDeleteWidget = (
    widgetId: string,
    updatePosition: boolean = false
  ) => {
    if (
      currentPage.content[widgetId]?.type === widgetTypes.Banner &&
      updatePosition
    ) {
      const currentBannerHeight = currentPage.content[widgetId].config.height;
      updateWidgetPosition(widgetId, 0, currentBannerHeight);
    }
    if (
      props.currentPage.content[widgetId]?.type === widgetTypes.RepeaterWidget
    ) {
      deleteContentIfExists(props.siteId, props.currentPage.pageId, widgetId)
        .then(() => {
          toast('Content Deleted successfully', {
            type: 'success',
            theme: 'colored',
          });
        })
        .catch(() => {
          toast('Error Deleting content', {
            type: 'error',
            theme: 'colored',
          });
        });
    }
    if (currentPage.content[widgetId]?.type === widgetTypes.CalendarWidget) {
      // call deleteCalendarWidget function
      deleteCalendarWidget(widgetId);
    }
    closeConfigPanel(false);
    const temp = _.cloneDeep(widgetList);
    const updatedWidgetList = temp.filter(item => item.uniqueId !== widgetId);
    setWidgetList(updatedWidgetList);
    setShowContextMenu(false); // Hide the context menu

    const removeByScope = (id: string) => {
      if (currentPage.content[id]?.defaultWidget && removeDefaultWidgets) {
        removeDefaultWidgets(id);
        return;
      }
      removeWidget(id);
    };

    // Delete matching widgets in sibling repeater containers
    syncDeleteAcrossRepeaterContainers({
      widgetId,
      currentPage,
      dispatch,
      site,
    });

    removeByScope(widgetId);

    const childWidgets = getAllChildrenRecursively(currentPage, widgetId);
    if (childWidgets.length > 0) {
      for (const childId of childWidgets) removeByScope(childId);
    }
    setResetToFalse();
    getActiveWidgetId('root');
  };

  const handleDuplicateWidget = (widgetId: string) => {
    closeConfigPanel(false);
    const { currentPage } = props;
    const widgetToDuplicate = currentPage.content[widgetId];

    if (widgetToDuplicate) {
      if (widgetToDuplicate.children.length > 0) {
        // recursively duplicate the widget and its children
        duplicateWidgetWithChildren({
          widgetToDuplicate,
          currentPage,
          addChild,
          parentWidgetId: widgetToDuplicate.config.parentId,
        });
      } else {
        const unique = uuidv4();
        const widgetConfig = {
          ...widgetToDuplicate.config,
          xLocation: widgetToDuplicate.config.xLocation + 10,
          yLocation: widgetToDuplicate.config.yLocation + 10,
          isWidgetDuplicated: true,
          parentId: widgetToDuplicate.config.parentId,
        };
        // Duplicating a sticky banner would create a second one, which PageLayout
        // can't render correctly (only one banner's children get relocated).
        if (
          widgetToDuplicate.type === widgetTypes.Banner &&
          widgetConfig.placement === STICKY_BOTTOM_PLACEMENT
        ) {
          widgetConfig.placement = BELOW_HEADER_PLACEMENT;
        }
        addChild(
          widgetToDuplicate.type,
          widgetConfig,
          0,
          unique,
          widgetToDuplicate.config.parentId,
          widgetToDuplicate.tabIndex
        );
        // Sync the duplicated widget to all sibling repeater containers
        syncAddAcrossRepeaterContainers({
          addedWidget: {
            id: unique,
            type: widgetToDuplicate.type,
            config: widgetConfig,
            children: [],
            tabIndex: widgetToDuplicate.tabIndex ?? -1,
          },
          currentPage,
          siteId,
          pageId: currentPage.pageId,
          dispatch,
        });
      }
    }
    setResetToFalse();
    setShowContextMenu(false);
  };

  const handleCustomWidget = (widgetState, currentPage, siteId) => {
    saveWidgetToMyWidgets({
      widgetState,
      currentPage,
      siteId,
      site,
      dispatch,
    });
  };

  function isDisabled(widgetId: string) {
    if (
      currentPage.content[widgetId].config?.action === 'submit' ||
      [HEADER_SECTION_ID, FOOTER_SECTION_ID, 'loginButton'].includes(
        currentPage.content[widgetId].id
      ) ||
      currentPage?.content[widgetId].config?.hasOwnProperty(
        'repeaterWidgetIndex'
      )
    ) {
      return true;
    }
  }

  const getWidgetWidth = (
    widgetType: string,
    width: number,
    direction: string,
    isFullScreenWidthEnabled: boolean
  ) => {
    const eleWidth =
      isFullScreenWidthEnabled &&
      ([
        widgetTypes.Image,
        widgetTypes.Navigation,
        widgetTypes.Section,
        widgetTypes.Banner,
        widgetTypes.TemplateHeaderFooterPlaceholder,
      ].includes(widgetType) ||
        (widgetTypes.Divider === widgetType && direction !== 'Vertical'))
        ? '100vw'
        : width;
    return eleWidth;
  };

  const getDirection = (widgetType: string) => {
    return widgetType === widgetTypes.Divider
      ? widgetState.config.direction
      : 'none';
  };

  const locationCoordsCopy = locationCoords;
  const layoutLocked = currentPage?.layoutLocked || false;

  function isConfigEnabled(widgetType: string): boolean {
    const disabledTypes = [
      widgetTypes.Application,
      widgetTypes.Banner,
      widgetTypes.CalendarWidget,
      widgetTypes.Divider,
      widgetTypes.Html,
      widgetTypes.RichText,
      widgetTypes.Section,
      widgetTypes.Text,
      widgetTypes.UploadWidget,
    ];
    return !disabledTypes.includes(widgetType);
  }

  const renderWidgetContent = (
    widgetType: string,
    content: React.ReactElement
  ) => {
    const trackedContent = injectTracking(content, (props as any).tracking);
    switch (widgetType) {
      case 'TextInput':
      case 'FileUploader':
        return <>{trackedContent}</>;
      case 'RichText':
        return <RichTextEditorSpan>{trackedContent}</RichTextEditorSpan>;
      default:
        return <StyledSpan>{trackedContent}</StyledSpan>;
    }
  };

  const handleControlClick = (widgetId: string) => {
    dispatch(setSelectedWidgetIds(widgetId));
  };

  const handleRegularClick = (widgetId: string) => {
    getActiveWidgetId(widgetId);
  };

  // Layering note: root-level widgets start at baseline z-index 0; nested widgets increase z-index relative to ancestor depth.
  // const getZIndex = (widgetType: string) => {
  //   switch (widgetType) {
  //     case widgetTypes.Navigation:
  //     case widgetTypes.Section:
  //     case widgetTypes.Tabs:
  //     case widgetTypes.AccordionWidget:
  //       return 0;
  //     default:
  //       return 1;
  //   }
  // };

  // Determines z-index based on ancestor count; root-level widgets start at 0.
  const getZIndex = (widgetId: string) => {
    const parentId = currentPage.content[widgetId]?.config?.parentId;
    return parentId ? getAncestorCount(currentPage, widgetId) : 0;
  };

  const sitePage = pages.find(p => p.id === currentPage.pageId);
  const widgetGroupingPayload = {
    ...sitePage,
    current: currentPage,
  };

  const handleOpenSettings = () => {
    openPopup(true);
    getActiveWidgetId(widgetId);
  };

  const handleDeleteWidgetContextMenu = () => {
    handleDeleteWidget(currentWidget, true);
  };

  const handleDuplicateWidgetContextMenu = () => {
    handleDuplicateWidget(currentWidget);
  };

  const handleDownloadWidgetContextMenu = () => {
    handleDownloadWidget(widgetState, currentPage);
  };

  const handleSaveToMyWidgets = () => {
    handleCustomWidget(widgetState, currentPage, siteId);
  };

  const handleGroupWidgets = () => {
    dispatch(groupWidgets(widgetGroupingPayload, siteId));
  };

  const handleUngroupWidgets = () => {
    dispatch(ungroupWidgets(widgetGroupingPayload, siteId));
  };

  if (!props.editingMode) {
    return null;
  } else {
    if (locationCoordsCopy !== undefined) {
      const isDefaultPage = currentPage.slug === 'defaultContentPage';
      return (
        <div
          onClick={e => e.stopPropagation()}
          className="widgetWrapperDiv"
          data-test-id="widget-wrapper"
        >
          {widgetList.map((item, idx) => {
            if (item === undefined) return;
            return (
              <div
                data-test-id={`context-menu-${item.uniqueId}`}
                onContextMenu={handleContextMenu}
                className={'contextDiv'}
                key={item.uniqueId}
              >
                {showLines && shouldDisplayGridLines && (
                  <>
                    <div
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '1px',
                        top: gridlinesPosition.y,
                        left: 0,
                        borderStyle: 'dashed',
                        borderColor: '#999',
                        pointerEvents: 'none',
                      }}
                    />

                    <div
                      style={{
                        position: 'absolute',
                        width: '1px',
                        height: '100%',
                        top: 0,
                        left: gridlinesPosition.x,
                        borderStyle: 'dashed',
                        borderColor: '#999',
                        pointerEvents: 'none',
                      }}
                    />
                  </>
                )}
                <StyledRnd
                  className={`rndDiv ${getFullScreenEnabled(item.widgetType) &&
                    'react-draggable-overwrite'}`}
                  ref={rndRef}
                  enableUserSelectHack={false}
                  style={{
                    ...style,
                    zIndex:
                      hoveredHorizontalNavId === item.uniqueId ||
                      hoveredSearchWidgetId === item.uniqueId
                        ? 9999
                        : getZIndex(item.uniqueId),
                  }}
                  size={{
                    width: getWidgetWidth(
                      item.widgetType,
                      item.width,
                      getDirection(item.widgetType),
                      getFullScreenEnabled(item.widgetType)
                    ),
                    height: item.height || 'auto',
                  }}
                  position={{
                    x: item.x,
                    y: isAccordionPanelOpen
                      ? getShiftedYposition(item.uniqueId, item.y)
                      : item.y,
                  }}
                  onDrag={(_e, d) => onDragWidget(d, item.uniqueId)}
                  onDragStop={(_e, d) => {
                    // Ensure Y position doesn't go above the original Y (item.y)
                    const updatedPosition = {
                      x: d.x,
                      y: Math.max(d.y, item.originalYLocation), // Prevent the Y position from going above its original position
                    };
                    updateLocation(
                      !isEditingTemplate && currentPage.layoutLocked
                        ? updatedPosition
                        : { x: d.x, y: d.y },
                      idx,
                      item.uniqueId,
                      item.widgetType
                    );
                  }}
                  onResizeStop={(_e, _dir, ref, _delta, position) => {
                    updateSize(
                      ref,
                      position,
                      idx,
                      item.uniqueId,
                      item.widgetType
                    );
                  }}
                  resizeGrid={[1, 1]}
                  dragGrid={[1, 1]}
                  minHeight={item.minHeight}
                  minWidth={item.minWidth}
                  enableResizing={getEnableResizing(item.widgetType)}
                  dragAxis={
                    !isEditingTemplate && currentPage.layoutLocked
                      ? 'y'
                      : 'both'
                  }
                  disableDragging={
                    isDefaultPage ||
                    item.uniqueId === HEADER_SECTION_ID ||
                    item.widgetType === widgetTypes.Banner ||
                    (item.widgetType ===
                      widgetTypes.TemplateHeaderFooterPlaceholder &&
                      item.uniqueId === HEADER_PLACEHOLDER_ID)
                  }
                  key={idx}
                  id={item.uniqueId}
                  onClick={_e => {
                    if (activeWidgetId !== item.uniqueId) {
                      openPopup(false);
                    }
                    if (_e.ctrlKey || _e.metaKey) {
                      // Handle control-click behavior
                      handleControlClick(item.uniqueId);
                    } else {
                      // Handle regular click behavior
                      handleRegularClick(item.uniqueId);
                    }
                  }}
                  onMouseDown={_e => {
                    if (_e.button === 2) {
                      const container = document.getElementById(
                        'editingContainerId'
                      );
                      const containerRect = container.getBoundingClientRect();

                      const relativeX = _e.clientX - containerRect.left;
                      const relativeY = _e.clientY - containerRect.top;

                      setContextMenuPosition({ x: relativeX, y: relativeY });
                      setCurrentWidget(item.uniqueId);
                      _e.preventDefault();
                    }
                  }}
                  onResize={(_e, _dir, _ref, _delta, position) => {
                    handleResize(position);
                  }}
                  onMouseOver={_e => {
                    setHoveredWidget(item.uniqueId);
                    if (isHorizontalNavWidget(item, currentPage)) {
                      setHoveredHorizontalNavId(item.uniqueId);
                    }
                    if (isSearchWidget(item)) {
                      setHoveredSearchWidgetId(item.uniqueId);
                    }
                  }}
                  onMouseOut={_e => {
                    setHoveredWidget(null);
                    if (
                      isHorizontalNavWidget(item, currentPage) &&
                      !_e.currentTarget.contains(_e.relatedTarget as Node)
                    ) {
                      setHoveredHorizontalNavId(null);
                    }
                    if (
                      isSearchWidget(item) &&
                      !_e.currentTarget.contains(_e.relatedTarget as Node)
                    ) {
                      setHoveredSearchWidgetId(null);
                    }
                  }}
                  fullScreenEnabled={getFullScreenEnabled(item.widgetType)}
                  itemXLocation={item.x}
                  itemYLocation={item.y}
                >
                  {renderWidgetContent(props.widgetState.type, item.content)}
                </StyledRnd>
              </div>
            );
          })}
          {showContextMenu && (
            <WidgetWrapperContextMenu
              contextMenuRef={contextMenuRef}
              contextMenuPosition={contextMenuPosition}
              widgetId={widgetId}
              widgetState={widgetState}
              layoutLocked={layoutLocked}
              selectedWidgetIds={selectedWidgetIds}
              onOpenSettings={handleOpenSettings}
              onDeleteWidget={handleDeleteWidgetContextMenu}
              onDuplicateWidget={handleDuplicateWidgetContextMenu}
              onDownloadWidget={handleDownloadWidgetContextMenu}
              onSaveToMyWidgets={handleSaveToMyWidgets}
              onGroupWidgets={handleGroupWidgets}
              onUngroupWidgets={handleUngroupWidgets}
              isDisabled={isDisabled}
              isConfigEnabled={isConfigEnabled}
            />
          )}
        </div>
      );
    } else {
      return (
        <div className="noWidgets">
          <h3>Get Started by Adding a New Section or Template</h3>
          <button>Add New Widgets</button>
        </div>
      );
    }
  }
};
